import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { addMinutes, differenceInMinutes, format } from 'date-fns';
import { tz } from '@date-fns/tz';
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';
import { CircularProgress, Stack, Typography } from '@mui/material';

import { selectedDeviceSafeAtom } from '../common/MapView';
import { deviceDataAtom } from '@/interfaces/devices';
import { deviceDataPeriodAtom } from '@/components/devices/DeviceDetail';
import { deviceAlarmHistoryAtom } from '@/interfaces/alarm';
import { deviceAuditLogAtom } from '@/interfaces/audit';
import { tempUnitAtom, timeZoneAtom } from '@/interfaces/utils';
import { getTemperatureText, getThresholdText } from '@/components/common/utils';
import DatePicker from '@/components/common/DatePicker';
import DeviceDetailGraph from './DeviceDetailGraph';
import LogTable from './LogTable';

import type { GraphData } from '@/@types/device';

export default function DeviceDetailOverview() {
  const { serialNumber, isHumiditySupported } = useAtomValue(selectedDeviceSafeAtom);
  const { data, isPending } = useAtomValue(deviceDataAtom);
  const { data: logs } = useAtomValue(deviceAuditLogAtom);
  const { data: alarmHistory } = useAtomValue(deviceAlarmHistoryAtom);
  const timeZone = useAtomValue(timeZoneAtom);
  const tempUnit = useAtomValue(tempUnitAtom);

  const { t, i18n } = useTranslation();

  const tempData = useMemo<GraphData[]>(() => {
    if (isPending) return [];
    const points = [];
    for (const index in data) {
      const i = Number(index);
      points.push({ x: data[i].time, y: data[i].temperature });
      if (i < data.length - 1 && differenceInMinutes(data[i + 1].time, data[i].time) > data[i].interval) {
        points.push({ x: addMinutes(data[i].time, data[i].interval).toISOString(), y: null });
      }
    }
    return points;
  }, [data, isPending]);
  const humidData = useMemo<GraphData[]>(() => {
    if (isPending || !isHumiditySupported) return [];
    const points = [];
    for (const index in data) {
      const i = Number(index);
      points.push({ x: data[i].time, y: data[i].humid });
      if (i < data.length - 1 && differenceInMinutes(data[i + 1].time, data[i].time) > data[i].interval) {
        points.push({ x: addMinutes(data[i].time, data[i].interval).toISOString(), y: null });
      }
    }
    return points;
  }, [data, isHumiditySupported, isPending]);

  const timeFormat = i18n.language.startsWith('ko') ? 'yyyy.MM.dd a hh:mm' : 'MM/dd/yyyy hh:mm a';
  const operationLogHeader = [t('Devices.label.time'), t('Common.label.description'), t('Common.label.user')];
  const alarmHistoryHeader = [
    t('Devices.label.time'),
    t('Devices.label.alarmType'),
    t('Devices.label.alarmData'),
    t('Devices.label.threshold'),
  ];
  const operationLogData: (string | number)[][] = useMemo(() => {
    if (!logs) return [];
    return logs.map((log) => [
      format(log.timestamp, timeFormat, { in: tz(timeZone) }).replace(' ', '\n'),
      log.message,
      log.email,
    ]);
  }, [logs, timeFormat, timeZone]);
  const alarmHistoryData: (string | number)[][] = useMemo(() => {
    if (!alarmHistory) return [];
    return alarmHistory.map((history) => [
      format(history.timestamp, timeFormat, { in: tz(timeZone) }),
      t(`Devices.label.${history.alarmType.toLowerCase()}`),
      getDataText(history.alarmType, tempUnit, history.data),
      getThresholdText(history, tempUnit),
    ]);
  }, [alarmHistory, t, tempUnit, timeFormat, timeZone]);

  return (
    <Stack gap={4} sx={{ flexGrow: 1, overflow: 'auto' }}>
      <Typography fontWeight="bold">
        {t('Devices.label.serialNumber')}: {serialNumber}
      </Typography>

      <DatePicker periodAtom={deviceDataPeriodAtom} />
      {isPending ? (
        <Stack justifyContent="center" alignItems="center" sx={{ aspectRatio: 2, bgcolor: 'white' }}>
          <CircularProgress color="primary" />
        </Stack>
      ) : (
        <DeviceDetailGraph tempData={tempData} humidData={humidData} />
      )}

      <Typography fontWeight="bold">{t('Log.label.operationLog')}</Typography>
      <LogTable header={operationLogHeader} data={operationLogData} />

      <Typography fontWeight="bold">{t('Devices.label.alarmHistory')}</Typography>
      <LogTable header={alarmHistoryHeader} data={alarmHistoryData} />
    </Stack>
  );
}

function getDataText(alarmType: string, tempUnit: string, value: string) {
  if (alarmType === 'Humidity' || alarmType === 'Battery') {
    return value + '%';
  }
  if (alarmType === 'Offline') {
    return value + t('Devices.label.minute');
  }
  if (alarmType === 'Temperature') {
    return getTemperatureText(value, tempUnit, true);
  }
}
